import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, useTheme, IconButton } from '@mui/material';
import { ErrorOutline, DragIndicator } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { ConfirmDialog } from '@/components/common';
import {
    transformQuestionType,
    getQuestionEditPaperStyles,
    shouldConfirmDeleteQuestion,
    isNumericQuestion,
} from '../../utils/campaignQuestionsEditorUtils';
import QuestionHint from './QuestionHint';
import MultiSelectOptions from '../MultiSelect/MultiSelectOptions';
import QuestionFormulaField from './QuestionFormulaField';
import QuestionActionButtons from './QuestionActionButtons';
import QuestionTextField from './QuestionTextField';
import QuestionTitleField from './QuestionTitleField';
import QuestionAnswerTypeField from './QuestionAnswerTypeField';
import RatingScaleToggle from './RatingScaleToggle';

import { QuestionType, type CampaignQuestion, type OptionsFieldError } from '../../types';

interface QuestionFieldProps {
    question: CampaignQuestion;
    onDelete: () => void;
    index?: number;
    error?: OptionsFieldError;
    dragHandleProps?: Record<string, unknown>;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    isFirst?: boolean;
    isLast?: boolean;
    isHighlighted?: boolean;
    fieldIndex: number;
}

/**
 * Single question field in the questions editor.
 */
const QuestionField: React.FC<QuestionFieldProps> = (props) => {
    const {
        question,
        onDelete,
        index = 0,
        error,
        dragHandleProps,
        onMoveUp,
        onMoveDown,
        isFirst,
        isLast,
        isHighlighted,
        fieldIndex,
    } = props;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const theme = useTheme();
    const formContext = useFormContext();

    const currentQuestion = useWatch({
        control: formContext.control,
        name: `questions.${fieldIndex}` as const,
        defaultValue: question,
    }) as CampaignQuestion;

    const currentQuestionType = currentQuestion.type;
    const numericQuestion = isNumericQuestion(currentQuestion) ? currentQuestion : null;

    const paperStyles = getQuestionEditPaperStyles(theme, isHighlighted);

    const handleDelete = () => {
        // Get the current form values to check if the question has content
        const formValues = formContext.getValues();
        const questionToDelete = fieldIndex !== undefined ? formValues.questions?.[fieldIndex] : question;

        if (shouldConfirmDeleteQuestion(questionToDelete || question)) {
            setConfirmDialogOpen(true);
            return;
        }

        onDelete();
    };

    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
    };

    const handleConfirmDelete = () => {
        setConfirmDialogOpen(false);
        onDelete();
    };

    const handleTypeChange = useCallback(
        (newType: QuestionType) => {
            // Get current form values for this question
            const questionToTransform = formContext.getValues(`questions.${fieldIndex}` as const);
            const updatedQuestion = transformQuestionType(questionToTransform, newType);

            // Update the form state directly without immediate validation
            formContext.setValue(`questions.${fieldIndex}` as const, updatedQuestion, {
                shouldValidate: false,
                shouldDirty: true,
            });
        },
        [fieldIndex, formContext]
    );

    const hasValidationError = Object.values(error || {}).some(Boolean);

    return (
        <Paper variant="outlined" sx={paperStyles} data-testid={`question-${index}`}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {dragHandleProps && (
                        <IconButton
                            {...dragHandleProps}
                            size="small"
                            sx={{
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                padding: 0.25,
                                marginLeft: -0.5,
                            }}
                            aria-label="Drag to reorder"
                        >
                            <DragIndicator fontSize="small" />
                        </IconButton>
                    )}
                    {hasValidationError && <ErrorOutline fontSize="small" color="error" />}
                    <Typography variant="h5">{`${index}. Question`}</Typography>
                </Box>
                <QuestionActionButtons
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    onDelete={handleDelete}
                    isFirst={isFirst}
                    isLast={isLast}
                />
            </Box>
            <QuestionTitleField question={question} error={error?.title} fieldIndex={fieldIndex} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {currentQuestionType !== QuestionType.FORMULA && (
                    <Box sx={{ flexGrow: 7, flexBasis: { xs: '100%', lg: '60%' } }}>
                        <QuestionTextField question={question} error={error?.text} fieldIndex={fieldIndex} />
                    </Box>
                )}
                <Box sx={{ flexGrow: 4, flexBasis: { xs: '100%', lg: '6rem' } }}>
                    <QuestionAnswerTypeField
                        questionId={question.id}
                        questionType={currentQuestionType}
                        index={index}
                        fieldIndex={fieldIndex}
                        error={error?.type}
                        onChange={handleTypeChange}
                    />
                    {currentQuestionType === QuestionType.NUMERIC && (
                        <RatingScaleToggle numericQuestion={numericQuestion} fieldIndex={fieldIndex} />
                    )}
                </Box>
            </Box>
            <QuestionHint question={question} />
            <Box sx={{ display: 'flex', gap: 1 }}>
                {currentQuestionType === QuestionType.MULTISELECT && (
                    <Box sx={{ flex: 1, minWidth: '300px' }}>
                        <MultiSelectOptions fieldIndex={fieldIndex} error={error as OptionsFieldError} />
                    </Box>
                )}
                {currentQuestionType === QuestionType.FORMULA && (
                    <Box sx={{ flex: 1, minWidth: '300px' }}>
                        <QuestionFormulaField fieldIndex={fieldIndex} error={error?.formula} />
                    </Box>
                )}
            </Box>
            <ConfirmDialog
                open={confirmDialogOpen}
                title="Delete question"
                message="Are you sure you want to delete this question? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </Paper>
    );
};

export default React.memo(QuestionField);
